import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Droplets, Check, Settings2, AlertTriangle, LayoutGrid, FileBarChart, ChevronDown, MessageSquareText } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { ExportButton } from "../components/ExportButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { ConfirmUndoDialog } from "../components/ConfirmUndoDialog";

import { useSegnalazioni, SegnalazioneButton, SegnalazioniBadge } from "../components/Segnalazioni";
import { useNavigate } from "react-router-dom";
import { MeseGiorniEsplosi, RecapOperatoriMese, RecapGiorno, AllertaBox } from "../components/MeseGiorniEsplosi";
import { UscitiInfoBox, isFuori, fuoriLabel, isDimesso } from "../components/UscitiInfoBox";
import { EsBadge } from "../components/EsBadge";
import { CorrettoBadge } from "../components/CorrettoBadge";
import { ElencoCorrezioniBtn, useCorrezioni } from "../components/ElencoCorrezioni";
import { toast } from "sonner";

// JS getDay(): 0=Dom .. 6=Sab
const WEEKDAYS = [[1, "Lun"], [2, "Mar"], [3, "Mer"], [4, "Gio"], [5, "Ven"], [6, "Sab"], [0, "Dom"]];
const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const startOfWeek = (d) => { const x = new Date(d); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); x.setHours(0, 0, 0, 0); return x; };
const weekDates = (base) => { const s = startOfWeek(base); return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; }); };
const fmtRange = (dates) => { const f = (d) => d.toLocaleDateString("it-IT", { day: "numeric", month: "short" }); return `${f(dates[0])} – ${f(dates[6])}`; };
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);
const fmtEstesoD = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); } catch { return iso || ""; } };
const fmtRelD = (iso) => {
  if (!iso) return "";
  try {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const d = new Date(iso + "T00:00:00");
    const diff = Math.round((t - d) / 86400000);
    if (diff === 0) return "oggi";
    if (diff === -1) return "domani";
    if (diff === 1) return "ieri";
    return d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long" });
  } catch { return iso; }
};
const prossimaDoccia = (sched) => {
  if (!sched || !sched.length) return null;
  for (let i = 1; i <= 7; i++) { const d = new Date(); d.setDate(d.getDate() + i); if (sched.includes(d.getDay())) return { data: ymd(d), giorni: i }; }
  return null;
};

export default function Docce() {
  const { selectedStructure, can } = useAuth();
  const { activeOperatore, opName, canSign, signerName, mustUnlock } = useFirma();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const today = ymd(new Date());
  const yesterday = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return ymd(d); })();
  const [cfg, setCfg] = useState(null); // { ospite_id, cognome, nome, giorni:[] }
  const [altroDlg, setAltroDlg] = useState(null); // { ospite_id, structure_id, nome, data, note }
  const [savingAltro, setSavingAltro] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(null);
  const [showDimessi, setShowDimessi] = useState(false);
  const [view, setView] = useState("griglia");
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));

  const { data } = useQuery({ queryKey: ["docce", selectedStructure, mese], queryFn: async () => (await api.get("/docce/grid", { params: { structure_id: selectedStructure, mese } })).data });
  const { data: report } = useQuery({ queryKey: ["docce-report", selectedStructure, mese], enabled: view === "report", queryFn: async () => (await api.get("/docce/report", { params: { structure_id: selectedStructure, mese } })).data });
  const { data: docceScadute = [] } = useQuery({ queryKey: ["docce-scadute", selectedStructure], queryFn: async () => (await api.get("/dashboard/docce-scadute", { params: { structure_id: selectedStructure } })).data });

  const toggle = async (r, day) => {
    const rec = records[`${r.ospite_id}_${day}`];
    const done = rec?.stato === "fatta";
    if (done) { if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; } }
    else if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    if (done) { setConfirmUndo({ nome: `${r.cognome} ${r.nome}`, extra: "doccia", action: () => doToggle(r, day, true) }); return; }
    doToggle(r, day, false);
  };
  const doToggle = async (r, day, done) => {
    try {
      await api.post("/docce/register", { ospite_id: r.ospite_id, structure_id: r.structure_id, data: day, stato: done ? "" : "fatta", operatore: signerName });
      qc.invalidateQueries({ queryKey: ["docce"] }); qc.invalidateQueries({ queryKey: ["correzioni"] });
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const openAltro = (r) => {
    const rec = records[`${r.ospite_id}_${openDay}`];
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    setAltroDlg({ ospite_id: r.ospite_id, structure_id: r.structure_id, nome: `${r.cognome} ${r.nome}`, data: openDay, note: rec?.stato === "altro" ? (rec?.note || "") : "" });
  };
  const saveAltro = async (remove = false) => {
    const c = altroDlg;
    if (!remove && !(c.note || "").trim()) { toast.error("Scrivi una nota (motivo)"); return; }
    setSavingAltro(true);
    try {
      await api.post("/docce/register", { ospite_id: c.ospite_id, structure_id: c.structure_id, data: c.data, stato: remove ? "" : "altro", note: remove ? "" : c.note.trim(), operatore: signerName });
      qc.invalidateQueries({ queryKey: ["docce"] }); qc.invalidateQueries({ queryKey: ["correzioni"] });
      setAltroDlg(null);
      toast.success(remove ? "Annullato" : "Nota salvata");
    } catch { toast.error("Errore nel salvataggio"); }
    setSavingAltro(false);
  };

  const saveCfg = async () => {
    try {
      const cutoff = (() => { const d = new Date(); d.setDate(d.getDate() - 14); return ymd(d); })();
      const extra = [...new Set(cfg.extra || [])].filter((iso) => iso >= cutoff).sort();
      const salta = [...new Set(cfg.salta || [])].filter((iso) => iso >= cutoff).sort();
      const touched = new Set([...extra, ...salta]);
      const note = {};
      Object.entries(cfg.noteMap || {}).forEach(([iso, t]) => { if (touched.has(iso) && iso >= cutoff) note[iso] = t; });
      const nota = (cfg.nota || "").trim();
      touched.forEach((iso) => { if (nota) note[iso] = nota; else delete note[iso]; });
      await api.put(`/ospiti/${cfg.ospite_id}`, { giorni_doccia: cfg.giorni, docce_extra: extra, docce_salta: salta, docce_note: note });
      qc.invalidateQueries({ queryKey: ["docce"] });
      setCfg(null);
      toast.success("Programma doccia aggiornato");
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const rows = data?.rows || []; const days = data?.days || []; const records = data?.records || {};
  const [openDay, setOpenDay] = useState(today);
  const doceCounts = {};
  const opCounts = {};
  Object.entries(records).forEach(([k, v]) => {
    if (v?.stato === "fatta") {
      const d = k.slice(k.lastIndexOf("_") + 1);
      doceCounts[d] = (doceCounts[d] || 0) + 1;
      const nome = v?.operatore || "—";
      opCounts[nome] = (opCounts[nome] || 0) + 1;
    }
  });
  const doceOperatori = Object.entries(opCounts).map(([nome, count]) => ({ nome, count })).sort((a, b) => b.count - a.count);
  const { byKey: segByKey, aperte: segAperte, refetch: segRefetch } = useSegnalazioni("docce", selectedStructure);
  const corrMap = useCorrezioni("docce", selectedStructure, openDay.slice(0, 7));
  // recap del giorno (openDay)
  const dowOpen = new Date(openDay).getDay();
  const fuoriIl = (r, day) => (r.fuori_giorni ? r.fuori_giorni.includes(day) : (isFuori(r) && (!r.fuori_dal || day >= r.fuori_dal)));
  const opCountsDay = {};
  let fatteOggi = 0;
  rows.forEach((r) => {
    if (fuoriIl(r, openDay)) return;
    const rec = records[`${r.ospite_id}_${openDay}`];
    if (rec?.stato === "fatta") { fatteOggi++; const n = rec.operatore || "—"; opCountsDay[n] = (opCountsDay[n] || 0) + 1; }
  });
  const doceOperatoriGiorno = Object.entries(opCountsDay).map(([nome, count]) => ({ nome, count })).sort((a, b) => b.count - a.count);
  const progrOggi = rows.filter((r) => !fuoriIl(r, openDay) && (((r.giorni_doccia || []).includes(dowOpen) && !(r.docce_salta || []).includes(openDay)) || (r.docce_extra || []).includes(openDay)));
  const daFareOggi = progrOggi.filter((r) => records[`${r.ospite_id}_${openDay}`]?.stato !== "fatta" && openDay <= today).length;
  const docceStatsGiorno = [
    { key: "fatte", label: "Fatte", value: fatteOggi, tone: "ok" },
    { key: "programmate", label: "Programmate oggi", value: progrOggi.length, tone: "muted" },
    { key: "da-fare", label: "Da fare", value: daFareOggi, tone: daFareOggi > 0 ? "warn" : "ok" },
  ];
  const dowLabel = (arr) => (arr || []).length ? WEEKDAYS.filter(([n]) => arr.includes(n)).map(([, l]) => l).join(" · ") : null;
  const dowNames = (arr) => (arr || []).length ? WEEKDAYS.filter(([n]) => arr.includes(n)).map(([, l]) => l).join(",") : "";
  // ospiti in ritardo: hanno giorni programmati e ultima doccia > 7 gg fa (o mai)
  const overdueCount = rows.filter((r) => (r.giorni_doccia || []).length && (!r.ultima || daysBetween(r.ultima, today) > 7)).length;
  const rRows = report?.rows || [];
  const docceCols = [
    { header: "Cognome", accessor: "cognome" },
    { header: "Nome", accessor: "nome" },
    { header: "Programma", accessor: (r) => dowNames(r.giorni_doccia) },
    { header: "Fatte", accessor: "fatte" },
    { header: "Programmate", accessor: "programmate" },
    { header: "Mancate", accessor: "mancate" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Droplets size={22} /> Docce</h1>
          <p className="text-sm text-muted-foreground">Programma settimanale delle docce: chi deve farla e chi è in ritardo.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {view === "griglia" && <SegnalazioniBadge categoria="docce" aperte={segAperte} />}
          <ElencoCorrezioniBtn categoria="docce" structureId={selectedStructure} mese={mese} />
          <div className="inline-flex rounded-lg border overflow-hidden">
            <button onClick={() => setView("griglia")} className={`px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1 ${view === "griglia" ? "bg-primary text-white" : "bg-white text-foreground"}`} data-testid="docce-tab-griglia"><LayoutGrid size={15} /> Griglia</button>
            <button onClick={() => setView("report")} className={`px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1 ${view === "report" ? "bg-primary text-white" : "bg-white text-foreground"}`} data-testid="docce-tab-report"><FileBarChart size={15} /> Report mensile</button>
          </div>
          {view === "griglia" && <>
            {canSign
              ? <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 px-3 py-1.5 text-sm font-semibold" data-testid="docce-operatore-attivo"><Check size={15} /> {signerName}</span>
              : <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3 py-1.5 text-sm font-semibold" data-testid="docce-operatore-mancante"><AlertTriangle size={15} /> Sblocca con la Firma Rapida</span>}
          </>}
        </div>
      </div>

      {view === "report" ? (
        <div className="gr-card p-4" data-testid="docce-report">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="font-head font-bold text-sm">Riepilogo mensile docce</span>
            <input type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="border rounded-md px-2 py-1 text-sm" data-testid="docce-report-mese" />
            <div className="ml-auto"><ExportButton filename={`docce-${mese}`} title="Report docce" subtitle={`Mese ${mese}`} columns={docceCols} rows={rRows} color="#2563eb" testid="docce-export" /></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
                <th className="text-left p-2">Ospite</th><th className="text-left p-2">Programma</th>
                <th className="text-right p-2">Fatte</th><th className="text-right p-2">Programmate</th><th className="text-right p-2">Mancate</th>
              </tr></thead>
              <tbody>
                {rRows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Nessun dato per il mese selezionato</td></tr>}
                {rRows.map((r) => (
                  <tr key={r.ospite_id} className="border-t" data-testid={`docce-report-row-${r.ospite_id}`}>
                    <td className="p-2 font-semibold"><button type="button" onClick={() => navigate(`/ospiti/${r.ospite_id}?tab=docce`)} className="hover:underline text-left" data-testid={`docce-nome-${r.ospite_id}`}>{r.cognome} {r.nome}</button><EsBadge id={r.ospite_id} /></td>
                    <td className="p-2 text-xs text-blue-700">{dowNames(r.giorni_doccia)}</td>
                    <td className="p-2 text-right tabnum font-semibold text-blue-700">{r.fatte}</td>
                    <td className="p-2 text-right tabnum text-muted-foreground">{r.programmate}</td>
                    <td className={`p-2 text-right tabnum font-semibold ${r.mancate > 0 ? "text-red-600" : "text-emerald-600"}`}>{r.mancate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3"><b>Fatte</b>: docce effettuate nel mese. <b>Programmate</b>: docce previste dal programma settimanale. <b>Mancate</b>: docce programmate fino a oggi non effettuate.</p>
        </div>
      ) : (
      <>
      {overdueCount > 0 && (
        <AllertaBox tone="warn" title="Docce arretrate — saltate nei giorni stabiliti" icon={AlertTriangle}
          items={docceScadute} testid="docce-scadute-box" onItemClick={(it) => navigate(`/ospiti/${it.ospite_id}?tab=docce`)}
          renderItem={(it) => (<span><b>{it.cognome} {it.nome}</b> <span className="ml-1 text-red-700 font-bold">{it.giorni_ritardo === 1 ? "Doveva essere fatta ieri" : `Doveva essere fatta il ${fmtDate(it.scaduta_dal)} (${it.giorni_ritardo} giorni fa)`}</span>{it.motivo ? <span className="block text-purple-800 font-semibold text-xs">Non fatta: {it.motivo}{it.motivo_op ? ` · ✍️ ${it.motivo_op}` : ""}</span> : null}</span>)} />
      )}

      <MeseGiorniEsplosi embedded color="#2563eb"
        mese={mese} onMese={setMese} openDay={openDay} onOpenDay={setOpenDay}
        counts={doceCounts} countLabel="docce fatte"
        monthTop={<RecapOperatoriMese operatori={doceOperatori} color="#2563eb" label="docce fatte" icon={Droplets} />}>
        <RecapGiorno operatori={doceOperatoriGiorno} stats={docceStatsGiorno} color="#2563eb" opLabel="docce fatte" />
        <UscitiInfoBox usciti={rows.filter(isFuori)} testid="docce-usciti-box" />
        {(() => {
          const renderRow = (r, num) => {
            const sched = r.giorni_doccia || [];
            const extra = r.docce_extra || [];
            const salta = r.docce_salta || [];
            const rec = records[`${r.ospite_id}_${openDay}`];
            const done = rec?.stato === "fatta";
            const isAltro = rec?.stato === "altro";
            const dow = new Date(openDay).getDay();
            const isExtraDay = extra.includes(openDay) && !sched.includes(dow);
            const isSaltataDay = sched.includes(dow) && salta.includes(openDay);
            const scheduledDay = (sched.includes(dow) && !salta.includes(openDay)) || extra.includes(openDay);
            const dueMissed = scheduledDay && !done && openDay <= today;
            const dim = isDimesso(r);
            const fuori = isFuori(r);
            const fuoriDal = r.fuori_dal || null;
            const fuoriOggi = fuoriIl(r, openDay); // assenza copre la mattina del giorno selezionato
            return (
              <div key={r.ospite_id} className={`gr-card p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 ${fuori ? "bg-amber-50/60 border-amber-200" : ""}`} data-testid={`docce-row-${r.ospite_id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1 w-full">
                  <span className="shrink-0 grid place-items-center h-6 w-6 rounded-full bg-secondary text-muted-foreground text-[11px] font-bold tabnum">{num != null ? num : "–"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold"><button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/ospiti/${r.ospite_id}?tab=docce`); }} className="hover:underline text-left" data-testid={`docce-row-nome-${r.ospite_id}`}>{r.cognome} {r.nome}</button><EsBadge id={r.ospite_id} /><CorrettoBadge correzioni={corrMap[`${r.ospite_id}_${openDay}`]} testid={`docce-corretto-${r.ospite_id}`} /></span>
                      {dim && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0">Dimesso</span>}
                      {fuori && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${r.presenza === "ospedale" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`docce-fuori-badge-${r.ospite_id}`}>{fuoriLabel(r)}{fuoriDal ? ` dal ${fmtDate(fuoriDal)}` : ""}</span>}
                      <button onClick={() => { const nmap = r.docce_note || {}; const wk = weekDates(new Date()).map(ymd); const existingNote = wk.map((iso) => nmap[iso]).find(Boolean) || ""; setCfg({ ospite_id: r.ospite_id, cognome: r.cognome, nome: r.nome, giorni: [...sched], extra: [...(r.docce_extra || [])], salta: [...(r.docce_salta || [])], noteMap: { ...nmap }, nota: existingNote }); }} className="text-muted-foreground hover:text-primary" title="Imposta giorni doccia" data-testid={`docce-cfg-${r.ospite_id}`}><Settings2 size={14} /></button>
                    </div>
                    <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap">
                      {dowLabel(sched) ? <span className="text-blue-700 bg-blue-50 rounded px-1.5 py-0.5">Prog: {dowLabel(sched)}</span> : <span className="text-muted-foreground">Nessun programma</span>}
                      {isExtraDay && <span className="text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5 font-semibold">· doccia extra{r.docce_note?.[openDay] ? ` (${r.docce_note[openDay]})` : ""}</span>}
                      {isSaltataDay && <span className="text-red-600 bg-red-50 rounded px-1.5 py-0.5 font-semibold">· saltata questa settimana{r.docce_note?.[openDay] ? ` (${r.docce_note[openDay]})` : ""}</span>}
                      {scheduledDay && !isExtraDay && !done && <span className="text-amber-700 font-semibold">· programmata questo giorno</span>}
                      {done && rec?.operatore && <span className="text-emerald-700">· fatta da {rec.operatore}</span>}
                      {isAltro && <span className="text-purple-700 bg-purple-50 rounded px-1.5 py-0.5 sm:hidden" data-testid={`docce-altro-note-${r.ospite_id}`}>Non fatta: {rec.note}{rec.operatore ? ` · ${rec.operatore}` : ""}</span>}
                    </div>
                  </div>
                </div>
                <div className="hidden sm:flex items-start gap-5 shrink-0" data-testid={`docce-cols-${r.ospite_id}`}>
                  <div className="w-36">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Ultima doccia</div>
                    <div className="text-xs font-semibold capitalize">{r.ultima ? fmtEstesoD(r.ultima) : <span className="text-red-600">Mai</span>}</div>
                    {r.ultima && r.ultima_operatore && <div className="text-[10px] text-muted-foreground">✍️ {r.ultima_operatore}</div>}
                  </div>
                  <div className="w-24">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Da quanti giorni</div>
                    <div className="text-xs font-bold">{r.ultima ? (daysBetween(r.ultima, today) === 0 ? <span className="text-green-600">oggi</span> : <span className={daysBetween(r.ultima, today) > 7 ? "text-red-600" : "text-foreground"}>da {daysBetween(r.ultima, today)} gg</span>) : <span className="text-red-600">—</span>}</div>
                  </div>
                  <div className="w-28">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Prossima doccia</div>
                    {(() => { const p = prossimaDoccia(sched); return p ? <div className="text-xs font-semibold text-blue-700">tra {p.giorni} gg<div className="text-[10px] text-muted-foreground font-normal capitalize">{fmtRelD(p.data)}</div></div> : <div className="text-xs text-muted-foreground">—</div>; })()}
                  </div>
                  <div className="w-36">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Non fatta</div>
                    {r.ultima_non_fatta ? (
                      <div className="text-xs font-semibold text-purple-700 leading-tight">
                        <span className="capitalize">{fmtRelD(r.ultima_non_fatta.data)}</span>
                        {r.ultima_non_fatta.note && <span className="block font-normal text-foreground">{r.ultima_non_fatta.note}</span>}
                        {r.ultima_non_fatta.operatore && <span className="block text-[10px] text-muted-foreground font-normal">✍️ {r.ultima_non_fatta.operatore}</span>}
                      </div>
                    ) : <div className="text-xs text-muted-foreground">—</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button onClick={() => toggle(r, openDay)} disabled={fuoriOggi && !done}
                    className={`h-11 flex-1 sm:flex-none px-4 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${done ? "bg-blue-600 text-white" : fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : dueMissed ? "bg-amber-100 ring-2 ring-amber-400 text-amber-700" : "bg-secondary hover:bg-blue-100"}`}
                    title={fuoriOggi && !done ? "Ospite fuori struttura in questa data" : undefined}
                    data-testid={`docce-cell-${r.ospite_id}-${openDay}`}>
                    {done ? <><Check size={16} /> Fatta</> : fuoriOggi ? <>Fuori struttura</> : dueMissed ? <><AlertTriangle size={14} /> Da fare</> : <><Droplets size={14} /> Segna fatta</>}
                  </button>
                  <button onClick={() => openAltro(r)} disabled={fuoriOggi}
                    className={`h-11 px-3 rounded-lg text-xs font-semibold flex flex-col items-center justify-center leading-none gap-0.5 transition-colors border ${fuoriOggi ? "bg-slate-200 text-slate-400 border-slate-200 cursor-not-allowed" : isAltro ? "bg-purple-600 text-white border-purple-600" : "bg-white text-purple-700 border-purple-200 hover:bg-purple-50"}`}
                    title="Spiega perché la doccia non è stata fatta" data-testid={`docce-altro-${r.ospite_id}`}>
                    <span className="inline-flex items-center gap-1"><MessageSquareText size={14} /> Non Fatta</span>
                    <span className="text-[8px] font-semibold opacity-80">spiega il perchè</span>
                  </button>
                  <SegnalazioneButton categoria="docce" structureId={selectedStructure} data={openDay}
                    ospiteId={r.ospite_id} ospiteNome={`${r.cognome} ${r.nome}`}
                    entry={segByKey[`${r.ospite_id}_${openDay}`]} onChanged={segRefetch} size={16} />
                </div>
              </div>
            );
          };
          if (rows.length === 0) return <p className="text-sm text-muted-foreground" data-testid="docce-giorno-list">Nessun ospite</p>;
          const dow = new Date(openDay).getDay();
          const isSched = (r) => (((r.giorni_doccia || []).includes(dow) && !(r.docce_salta || []).includes(openDay)) || (r.docce_extra || []).includes(openDay));
          const attiviRows = rows.filter((r) => !isDimesso(r));
          const dimessi = rows.filter(isDimesso);
          const prog = attiviRows.filter(isSched);
          const altri = attiviRows.filter((r) => !isSched(r));
          return (
            <div className="space-y-5" data-testid="docce-giorno-list">
              <section data-testid="docce-programmati-oggi">
                <h4 className="text-sm font-head font-bold text-blue-700 mb-2 flex items-center gap-1.5"><Droplets size={15} /> Programmati oggi <span className="text-xs font-normal text-muted-foreground">({prog.length})</span></h4>
                {prog.length === 0
                  ? <p className="text-sm text-muted-foreground">Nessuna doccia programmata per questo giorno.</p>
                  : <div className="space-y-2">{prog.map((r, i) => renderRow(r, i + 1))}</div>}
              </section>
              {altri.length > 0 && (
                <section data-testid="docce-altri-ospiti">
                  <h4 className="text-sm font-head font-bold text-muted-foreground mb-2">Altri ospiti <span className="text-xs font-normal">({altri.length})</span></h4>
                  <div className="space-y-2">{altri.map((r, i) => renderRow(r, i + 1))}</div>
                </section>
              )}
              {dimessi.length > 0 && (
                <section data-testid="docce-dimessi-section">
                  <button onClick={() => setShowDimessi((v) => !v)} data-testid="docce-toggle-dimessi" className="w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg bg-orange-50 border border-orange-200">
                    <ChevronDown size={16} className={`text-orange-700 transition-transform ${showDimessi ? "" : "-rotate-90"}`} />
                    <span className="font-bold text-sm text-orange-800">Dimessi</span>
                    <span className="text-xs font-semibold text-orange-700 bg-orange-200 rounded-full px-2 py-0.5 tabnum">{dimessi.length}</span>
                  </button>
                  {showDimessi && <div className="space-y-2 mt-2">{dimessi.map((r) => renderRow(r, null))}</div>}
                </section>
              )}
            </div>
          );
        })()}
        <div className="text-xs text-muted-foreground mt-3 flex flex-wrap gap-4">
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-blue-600 inline-block" /> Doccia effettuata</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-100 ring-2 ring-amber-400 inline-block" /> Programmata da fare</span>
          <span>Imposta i giorni con l'icona ingranaggio. Tocca "Segna fatta" per registrare (firma con l'operatore sbloccato via PIN).</span>
        </div>
      </MeseGiorniEsplosi>
      </>
      )}

      <Dialog open={!!cfg} onOpenChange={(o) => !o && setCfg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Programma doccia</DialogTitle>
            <DialogDescription>{cfg?.cognome} {cfg?.nome}</DialogDescription>
          </DialogHeader>

          {/* Programma fisso settimanale */}
          <div className="py-1">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Programma fisso settimanale</div>
            <p className="text-xs text-muted-foreground mb-2">Giorni in cui è prevista la doccia ogni settimana.</p>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map(([n, l]) => {
                const on = cfg?.giorni?.includes(n);
                return (
                  <button key={n} data-testid={`docce-day-${n}`}
                    onClick={() => setCfg((p) => ({ ...p, giorni: on ? p.giorni.filter((x) => x !== n) : [...p.giorni, n] }))}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold border ${on ? "bg-primary text-white border-primary" : "bg-white text-foreground border-input hover:bg-secondary"}`}>
                    {l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Solo per questa settimana */}
          {cfg && (() => {
            const wd = weekDates(new Date());
            return (
              <div className="pt-3 mt-1 border-t">
                <div className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-1">Solo per questa settimana <span className="font-normal text-muted-foreground normal-case">({fmtRange(wd)})</span></div>
                <p className="text-xs text-muted-foreground mb-2">Tocca un giorno per <b>aggiungere una doccia extra</b> (fuori programma) o <b>saltare</b> quella prevista. Vale solo questa settimana.</p>
                <div className="grid grid-cols-7 gap-1.5">
                  {wd.map((d) => {
                    const iso = ymd(d);
                    const dow = d.getDay();
                    const isFixed = cfg.giorni.includes(dow);
                    const isSalta = cfg.salta?.includes(iso);
                    const isExtra = cfg.extra?.includes(iso);
                    let cls = "bg-white text-slate-400 border-input hover:bg-secondary", tag = "libero";
                    if (isExtra) { cls = "bg-emerald-600 text-white border-emerald-600"; tag = "extra"; }
                    else if (isFixed && !isSalta) { cls = "bg-blue-600 text-white border-blue-600"; tag = "prevista"; }
                    else if (isFixed && isSalta) { cls = "bg-red-50 text-red-600 border-red-200 line-through"; tag = "saltata"; }
                    const onClick = () => setCfg((p) => {
                      if (isFixed) return { ...p, salta: isSalta ? p.salta.filter((x) => x !== iso) : [...(p.salta || []), iso] };
                      return { ...p, extra: isExtra ? p.extra.filter((x) => x !== iso) : [...(p.extra || []), iso] };
                    });
                    return (
                      <button key={iso} onClick={onClick} data-testid={`docce-week-${iso}`}
                        className={`rounded-lg border py-1.5 flex flex-col items-center leading-tight ${cls}`}>
                        <span className="text-[10px] font-semibold">{WEEKDAYS.find(([n]) => n === dow)?.[1]}</span>
                        <span className="text-sm font-bold tabnum">{d.getDate()}</span>
                        <span className="text-[8px] font-semibold opacity-90 capitalize">{tag}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-blue-600 inline-block" /> Prevista</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-emerald-600 inline-block" /> Extra (fuori programma)</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-red-200 inline-block" /> Saltata</span>
                </div>
                {((cfg.extra || []).length > 0 || (cfg.salta || []).length > 0) && (
                  <div className="mt-3">
                    <label className="text-xs font-semibold text-muted-foreground">Nota (facoltativa)</label>
                    <Textarea rows={2} value={cfg.nota || ""} onChange={(e) => setCfg((p) => ({ ...p, nota: e.target.value }))} placeholder="Es. doccia spostata per visita medica" className="mt-1" data-testid="docce-week-nota" />
                  </div>
                )}
              </div>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCfg(null)}>Annulla</Button>
            <Button onClick={saveCfg} data-testid="docce-cfg-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!altroDlg} onOpenChange={(o) => !o && setAltroDlg(null)}>
        <DialogContent data-testid="docce-altro-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquareText size={18} className="text-purple-600" /> Doccia non fatta — {altroDlg?.nome}</DialogTitle>
            <DialogDescription>Indica perché la doccia non è stata fatta (es. l'ospite non vuole, è indisposto, ecc.). La nota resta firmata.</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <Textarea rows={4} value={altroDlg?.note ?? ""} onChange={(e) => setAltroDlg((p) => ({ ...p, note: e.target.value }))} placeholder="Es. L'ospite ha rifiutato la doccia oggi." data-testid="docce-altro-note-input" />
          </div>
          <DialogFooter className="flex-wrap gap-2">
            {altroDlg && records[`${altroDlg.ospite_id}_${altroDlg.data}`]?.stato === "altro" && (
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 mr-auto" onClick={() => saveAltro(true)} disabled={savingAltro} data-testid="docce-altro-remove">Rimuovi</Button>
            )}
            <Button variant="outline" onClick={() => setAltroDlg(null)}>Annulla</Button>
            <Button onClick={() => saveAltro(false)} disabled={savingAltro || !(altroDlg?.note || "").trim()} data-testid="docce-altro-save"><Check size={15} className="mr-1" /> Salva nota</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmUndoDialog ctx={confirmUndo} onClose={() => setConfirmUndo(null)} />
    </div>
  );
}
